/**
 * Structural deltas over plain data.
 *
 * The realm is now simulated in one place and drawn in several, so the thing
 * that crosses the wire is *state* rather than orders. A realm's state is about
 * a hundred and fifty kilobytes of JSON — three hundred and eighty provinces,
 * thirty crowns, the book, the roads — and almost none of it moves in any given
 * tenth of a second. Sending all of it ten times a second would be a megabyte
 * and a half per player per second to say that four armies had walked a little
 * further down a road.
 *
 * So each update carries only what changed since the last one. The shape is
 * deliberately generic rather than hand-written per field: `CampaignSave` is
 * plain JSON-safe data by design — the save file's header says so in as many
 * words — and a hand-written encoder would be a second field list to keep in
 * step with `saveCampaign` and `hashCampaign`, which is exactly the drift those
 * two files warn about. A structural diff has no field list to rot.
 *
 * The patch shape, which is what actually goes over the wire:
 *
 *   `{ v: x }`            this value is now `x` — leaves, and any change of kind
 *   `{ o: {k: patch} }`   an object: these keys changed
 *   `{ o: {…}, x: [k] }`  …and these keys are gone
 *   `{ a: {i: patch} }`   an array: these indices changed
 *   `{ a: {…}, n: 12 }`   …and it is now twelve long
 *
 * `undefined` means *nothing changed*, which is what lets a whole untouched
 * subtree — the road graph, the province table between councils — cost nothing
 * at all rather than costing its size in "same" markers.
 */

export type Json = null | boolean | number | string | Json[] | { [k: string]: Json };

/** A patch as it travels. Opaque to everything but `diff` and `patch`. */
export interface Patch {
  /** A whole new value, for leaves and for a change of kind. */
  v?: Json;
  /** Changed keys of an object. */
  o?: Record<string, Patch>;
  /** Keys the object no longer has. */
  x?: string[];
  /** Changed indices of an array. */
  a?: Record<string, Patch>;
  /** The array's new length, when it changed. */
  n?: number;
}

const isObj = (v: unknown): v is Record<string, Json> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * What it would take to turn `a` into `b`, or `undefined` if they already agree.
 *
 * Recursion stops at the first pair that is identical, which is the whole
 * economy of this: a council that touched two provinces walks the other three
 * hundred and seventy-eight exactly once each and returns nothing for them.
 */
export function diff(a: Json | undefined, b: Json | undefined): Patch | undefined {
  if (a === b) return undefined;
  if (b === undefined) return undefined; // handled by the parent's `x` list

  if (Array.isArray(b)) {
    if (!Array.isArray(a)) return { v: b };
    let out: Record<string, Patch> | undefined;
    const shared = Math.min(a.length, b.length);
    for (let i = 0; i < shared; i++) {
      const d = diff(a[i], b[i]);
      if (d) (out ??= {})[i] = d;
    }
    // Anything past the old end is new outright; anything past the new end is
    // dropped by the length alone, which is why there is no `x` for arrays.
    for (let i = shared; i < b.length; i++) (out ??= {})[i] = { v: b[i] };
    if (!out && a.length === b.length) return undefined;
    const p: Patch = {};
    if (out) p.a = out;
    if (a.length !== b.length) p.n = b.length;
    return p;
  }

  if (isObj(b)) {
    if (!isObj(a)) return { v: b };
    let out: Record<string, Patch> | undefined;
    let gone: string[] | undefined;
    for (const k of Object.keys(b)) {
      const d = k in a ? diff(a[k], b[k]) : { v: b[k] };
      if (d) (out ??= {})[k] = d;
    }
    for (const k of Object.keys(a)) if (!(k in b)) (gone ??= []).push(k);
    if (!out && !gone) return undefined;
    const p: Patch = {};
    if (out) p.o = out;
    if (gone) p.x = gone;
    return p;
  }

  return { v: b };
}

/**
 * Apply a patch, in place wherever the shape allows.
 *
 * In place matters as much as the size on the wire: the alternative is rebuilding
 * a campaign's worth of objects ten times a second, and the panels reading it
 * hold on to what they were given between frames. The returned value is what the
 * *parent* should now hold — the same object it passed in, except where a leaf or
 * a change of kind means it cannot be.
 */
export function patch(base: Json | undefined, p: Patch | undefined): Json | undefined {
  if (!p) return base;
  if ('v' in p) return p.v as Json;

  if (p.a || p.n !== undefined) {
    if (!Array.isArray(base)) return base;
    if (p.a) {
      for (const k of Object.keys(p.a)) {
        const i = Number(k);
        base[i] = patch(base[i], p.a[k]) as Json;
      }
    }
    if (p.n !== undefined) base.length = p.n;
    return base;
  }

  if (p.o || p.x) {
    if (!isObj(base)) return base;
    if (p.o) {
      for (const k of Object.keys(p.o)) base[k] = patch(base[k], p.o[k]) as Json;
    }
    if (p.x) for (const k of p.x) delete base[k];
    return base;
  }

  return base;
}
